import {
  type ColorValue,
  CssColor,
  CssFont,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  AlignmentOptions,
  DisplayStyles,
  LayoutTypes,
} from '../../types/types';
import { COURSES_EXPERIMENT } from '../../utils/settings/consts';

type CssColorValue = Parameters<typeof wixColor>[0];
type CssFontValue = Parameters<typeof wixFont>[0];

export type IStylesParams = IStyleParamsBoolean &
  IStylesParamsNumbers &
  IStyleParamsColors &
  IStyleParamsFonts;

export type IStyleParamsBoolean = {
  headerTitleVisibility: StyleParamType.Boolean;
  headerServiceFilterVisibility: StyleParamType.Boolean;
  headerStaffFilterVisibility: StyleParamType.Boolean;
  headerLocationFilterVisibility: StyleParamType.Boolean;
  headerTimezoneSelectionVisibility: StyleParamType.Boolean;
  tableDayDividerVisibility: StyleParamType.Boolean;
  tableSessionDividerVisibility: StyleParamType.Boolean;
  sessionServiceImageVisibility: StyleParamType.Boolean;
  sessionDurationVisibility: StyleParamType.Boolean;
  sessionStaffImageVisibility: StyleParamType.Boolean;
  sessionReadMoreLinkVisibility: StyleParamType.Boolean;
  sessionLocationVisibility: StyleParamType.Boolean;
  sessionStaffMemberVisibility: StyleParamType.Boolean;
  sessionPriceVisibility: StyleParamType.Boolean;
  headerServiceTypeFilterVisibility: StyleParamType.Boolean;
  headerCourseStatusFilterVisibility: StyleParamType.Boolean;
  sessionCourseBadgeVisibility: StyleParamType.Boolean;
  sessionCourseStatusVisibility: StyleParamType.Boolean;
  sessionCourseDateRangeVisibility: StyleParamType.Boolean;
  sessionPriceBeforeDiscountVisibility: StyleParamType.Boolean;
  sessionDiscountNameVisibility: StyleParamType.Boolean;
  sessionNumberOfSpotsLeftVisibility: StyleParamType.Boolean;
  canBookFromDailyAgenda: StyleParamType.Boolean;
};

export type IStyleParamsFonts = {
  headerTitleFont: StyleParamType.Font;
  datePickerDatesFont: StyleParamType.Font;
  filtersFont: StyleParamType.Font;
  tableDatesFont: StyleParamType.Font;
  tableDayFont: StyleParamType.Font;
  sessionsItemsServiceNameFont: StyleParamType.Font;
  sessionsItemsPrimaryFont: StyleParamType.Font;
  sessionsItemsSecondaryFont: StyleParamType.Font;
  sessionItemsRegularButtonFont: StyleParamType.Font;
  loadMoreButtonFont: StyleParamType.Font;
  sessionDiscountNameFont: StyleParamType.Font;
  sessionCourseBadgeTextFont: StyleParamType.Font;
};

export type IStylesParamsNumbers = {
  generalBorderWidth: StyleParamType.Number;
  generalWidgetCornerRadius: StyleParamType.Number;
  tableDayDividerWidth: StyleParamType.Number;
  tableSessionDividerWidth: StyleParamType.Number;
  sessionItemsCornerRadius: StyleParamType.Number;
  sessionItemsImageCornerRadius: StyleParamType.Number;
  sessionsItemsRegularButtonBorderWidth: StyleParamType.Number;
  sessionsItemsRegularCornerRadius: StyleParamType.Number;
  loadMoreButtonBorderWidth: StyleParamType.Number;
  loadMoreButtonCornerRadius: StyleParamType.Number;
  layoutHeaderSidePadding: StyleParamType.Number;
  layoutHeaderTopPadding: StyleParamType.Number;
  layoutHeaderBottomPadding: StyleParamType.Number;
  dayLayoutTopAndBottomPadding: StyleParamType.Number;
  dayLayoutsidePadding: StyleParamType.Number;
  dayLayoutpaddingBetweenSessionsAndDates: StyleParamType.Number;
  dayLayoutpaddingBetweenSessionsAndDatesOverlay: StyleParamType.Number;
  layoutImageSize: StyleParamType.Number;
  dayLayoutPaddingBetweenHoursAndItemsOverlay: StyleParamType.Number;
  itemsLayoutItemHeightOverlay: StyleParamType.Number;
  itemsLayoutSidePaddingOverlay: StyleParamType.Number;
  itemsLayoutTopAndBottomPadding: StyleParamType.Number;
  itemsLayoutSpaceBetweenItems: StyleParamType.Number;
};

export type IStyleParamsColors = {
  headerTitleColor: StyleParamType.Color;
  datePickerDatesColor: StyleParamType.Color;
  selectionAndHighlightColor: StyleParamType.Color;
  datePickerSelectedColor: StyleParamType.Color;
  datePickerDisabledColor: StyleParamType.Color;
  filtersColor: StyleParamType.Color;
  tableDateColor: StyleParamType.Color;
  tableDayColor: StyleParamType.Color;
  tableDayDividerOpacity: StyleParamType.Color;
  tableMobileDayDividerColor: StyleParamType.Color;
  generalBorderOpacityAndColor: StyleParamType.Color;
  tableSessionDividerOpacity: StyleParamType.Color;
  generalWidgetBackgroundColor: StyleParamType.Color;
  sessionItemsBackgroundColor: StyleParamType.Color;
  sessionItemsImageOverlayOpacityAndColor: StyleParamType.Color;
  sessionsItemsButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionsItemsDisabledButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionsItemsButtonBackgroundColor: StyleParamType.Color;
  sessionsItemsDisabledButtonBackgroundColor: StyleParamType.Color;
  sessionsItemsTextColor: StyleParamType.Color;
  sessionsItemsDisabledTextColor: StyleParamType.Color;
  sessionItemsRegularButtonColor: StyleParamType.Color;
  sessionsItemsRegularButtonBackgroundAndOpacityColor: StyleParamType.Color;
  sessionsItemsRegularButtonBorderAndOpacityColor: StyleParamType.Color;
  loadMoreButtonColor: StyleParamType.Color;
  loadMoreButtonBackgroundAndOpacityColor: StyleParamType.Color;
  loadMoreButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionItemsPrimaryTextColor: StyleParamType.Color;
  sessionItemsServiceNameTextColor: StyleParamType.Color;
  sessionItemsSecondaryTextColor: StyleParamType.Color;
  sessionItemsOnImageServiceNameTextColor: StyleParamType.Color;
  sessionItemsOnImagePrimaryTextColor: StyleParamType.Color;
  sessionDiscountNameColor: StyleParamType.Color;
  sessionCourseBadgeTextColor: StyleParamType.Color;
  sessionCourseBadgeBackgroundColor: StyleParamType.Color;
  sessionCourseBadgeOnImageTextColor: StyleParamType.Color;
  sessionCourseBadgeOnImageBackgroundColor: StyleParamType.Color;
};

export type ICustomStylesParam = {
  displayHeaderTitle: string;
  displayHeaderServiceFilter: string;
  displayHeaderStaffFilter: string;
  displayHeaderLocationFilter: string;
  displayHeaderTimezoneSelection: string;
  displaySessionServiceImage: string;
  displaySessionDuration: string;
  displaySessionStaffImage: string;
  displaySessionReadMoreLink: string;
  displaySessionLocation: string;
  displaySessionStaffMember: string;
  displaySessionPrice: string;
  displayHeaderServiceTypeFilter: string;
  displayHeaderCourseStatusFilter: string;
  displaySessionCourseBadge: string;
  displaySessionCourseStatus: string;
  displaySessionCourseDateRange: string;
  displaySessionPriceBeforeDiscount: string;
  displaySessionDiscountName: string;
  displaySessionStaffAndLocationWrapper: string;
  displaySessionNumberOfSpotsLeft: string;
  displayTableDayDivider: string;
  displayTableSessionDivider: string;
  displayBookFromDailyAgenda: string;
  agendaAlignedItem: string;
  agendaAlignItemsOverlay: string;
  spaceBetweenSessions: number;
};

export type ICustomStyleParamsInput = {
  strings: never;
  colors: { [key in keyof IStyleParamsColors]: string };
  numbers: { [key in keyof IStylesParamsNumbers]: number };
  booleans: { [key in keyof IStyleParamsBoolean]: boolean };
  fonts: { [key in keyof IStyleParamsFonts]: string };
};

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

const booleanParam = (defaultValue: boolean): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  },
});

/** A visibility default that follows the experiment; existing sites keep `whenOff`. */
const coursesBooleanParam = (
  whenOff: boolean,
  whenOn: boolean,
): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: ({ experiments } = {} as any) =>
    experiments?.enabled(COURSES_EXPERIMENT) ? whenOn : whenOff,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: whenOff,
    },
  },
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor?: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: themeColor
      ? wixColor(themeColor)
      : {
          type: 'static',
          preset,
        },
  },
});

const fixedColorParam = (
  type: StyleTypeFor<ColorValue>,
  color: string,
  opacity = 1,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: () => ({ value: color, opacity, name: null }),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset: color,
      opacity,
    },
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor?: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: themeColor
      ? wixColor(themeColor)
      : {
          type: 'static',
          preset,
          opacity,
        },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont?: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'static',
          preset,
          ...props,
        },
  },
});

const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset,
  mobileSize: number,
  size: number,
  themeFont?: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({
    textPresets,
    isMobile,
  }: {
    textPresets: ISiteTextPreset;
    isMobile: boolean;
  }) =>
    wixFontParam(preset, { size: isMobile ? mobileSize : size })({
      textPresets,
    }),
  metadata: {
    type: StyleType.font,
    defaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'static',
          preset,
          size,
        },
    mobileDefaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'static',
          preset,
          size: mobileSize,
        },
  },
});

export const styleParamsDefinitions: {
  [key in keyof IStylesParams]: IStyleParam;
} = {
  layoutHeaderSidePadding: numberParam(StyleType.padding, 20),
  layoutHeaderTopPadding: numberParam(StyleType.paddingTop, 20),
  layoutHeaderBottomPadding: numberParam(StyleType.paddingBottom, 20),
  dayLayoutTopAndBottomPadding: numberParam(StyleType.padding, 16),
  dayLayoutsidePadding: numberParam(StyleType.padding, 20),
  dayLayoutpaddingBetweenSessionsAndDates: numberParam(StyleType.gap, 40),
  dayLayoutpaddingBetweenSessionsAndDatesOverlay: numberParam(
    StyleType.gap,
    200,
  ),
  layoutImageSize: numberParam(StyleType.width, 80),
  generalBorderWidth: numberParam(StyleType.borderWidth, 0),
  loadMoreButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  loadMoreButtonCornerRadius: numberParam(StyleType.borderRadius, 1),
  sessionsItemsRegularButtonBorderWidth: numberParam(StyleType.borderWidth, 0),
  sessionsItemsRegularCornerRadius: numberParam(StyleType.borderRadius, 1),
  sessionItemsCornerRadius: numberParam(StyleType.borderRadius, 0),
  sessionItemsImageCornerRadius: numberParam(StyleType.borderRadius, 0),
  tableDayFont: fontParam('Body-M', { size: 16 }, CssFont.WstParagraph2Font),
  loadMoreButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  loadMoreButtonColor: colorParam(
    StyleType.color,
    'button-color-fill-primary',
    CssColor.WstButtonSecondaryColor,
  ),
  loadMoreButtonBackgroundAndOpacityColor: colorParam(
    StyleType.backgroundColor,
    'color-fill-background-primary',
    CssColor.WstButtonSecondaryBackgroundColor,
  ),
  loadMoreButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'button-color-fill-primary',
    CssColor.WstButtonSecondaryBorderBottomColor,
  ),
  sessionItemsRegularButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  sessionItemsRegularButtonColor: colorParamWithOpacity(
    StyleType.color,
    'color-fill-background-primary',
    1,
    CssColor.WstButtonPrimaryColor,
  ),
  sessionItemsPrimaryTextColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstParagraph2Color,
  ),
  sessionItemsServiceNameTextColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstHeading2Color,
  ),
  sessionItemsSecondaryTextColor: colorParam(
    StyleType.color,
    'color-text-secondary',
    CssColor.WstShade3Color,
  ),
  sessionDiscountNameColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  // The two tokens the badge already used, so a site that never opens the
  // Design tab keeps the badge it has today (HANDOFF 5.7, Figma 4235-117212).
  sessionCourseBadgeTextColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstParagraph2Color,
  ),
  // A preset `wixColorParam` cannot map resolves to solid BLACK, not to
  // nothing, so a test pins this slot name.
  sessionCourseBadgeBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-fill-base-shade-1',
    CssColor.WstShade1Color,
  ),
  // The OVERLAY layout only: a palette shade disappears into the image.
  sessionCourseBadgeOnImageBackgroundColor: fixedColorParam(
    StyleType.backgroundColor,
    '#FFFFFF',
    0.12,
  ),
  sessionCourseBadgeOnImageTextColor: fixedColorParam(
    StyleType.color,
    '#FFFFFF',
  ),
  sessionItemsOnImageServiceNameTextColor: fixedColorParam(
    StyleType.color,
    '#FFFFFF',
  ),
  sessionItemsOnImagePrimaryTextColor: fixedColorParam(
    StyleType.color,
    '#FFFFFF',
  ),
  sessionsItemsRegularButtonBackgroundAndOpacityColor: colorParam(
    StyleType.backgroundColor,
    'button-color-fill-primary',
    CssColor.WstButtonPrimaryColor,
  ),
  sessionsItemsRegularButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'button-color-fill-primary',
    CssColor.WstButtonPrimaryColor,
  ),
  sessionsItemsTextColor: colorParam(
    StyleType.color,
    'button-color-text-primary',
    CssColor.WstButtonPrimaryColor,
  ),
  sessionsItemsDisabledButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'button-color-fill-primary-disabled',
    CssColor.WstSystemDisabledColor,
  ),
  sessionsItemsDisabledTextColor: colorParam(
    StyleType.color,
    'color-fill-background-primary',
    CssColor.WstSystemDisabledColor,
  ),
  sessionsItemsButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'button-color-fill-primary',
    CssColor.WstSystemDisabledColor,
  ),
  sessionsItemsDisabledButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'button-color-fill-primary-disabled',
    CssColor.WstSystemDisabledColor,
  ),
  sessionsItemsButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'button-color-fill-primary',
    CssColor.WstButtonPrimaryColor,
  ),
  sessionsItemsSecondaryFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  sessionsItemsServiceNameFont: fontParam(
    'Page-title',
    { size: 24 },
    CssFont.WstHeading2Font,
  ),
  sessionsItemsPrimaryFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  sessionDiscountNameFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  // 12px, the override the badge hardcoded in its stylesheet.
  sessionCourseBadgeTextFont: fontParam(
    'Body-M',
    { size: 12 },
    CssFont.WstParagraph2Font,
  ),
  tableDayColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstParagraph2Color,
  ),
  sessionItemsBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'button-color-fill-primary',
    CssColor.WstButtonPrimaryColor,
  ),
  sessionItemsImageOverlayOpacityAndColor: fixedColorParam(
    StyleType.backgroundColor,
    'rgba(0, 0, 0, 0.6)',
    0.6,
  ),
  tableDatesFont: fontParamWithMobileDefault(
    'Body-M',
    16,
    20,
    CssFont.WstParagraph2Font,
  ),
  tableDateColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstParagraph2Color,
  ),
  tableDayDividerOpacity: colorParamWithOpacity(
    StyleType.color,
    'color-text-primary',
    1,
    CssColor.WstSystemLine1Color,
  ),
  tableMobileDayDividerColor: colorParamWithOpacity(
    StyleType.color,
    'color-text-primary',
    0.06,
    CssColor.WstParagraph2Color,
  ),
  generalBorderOpacityAndColor: colorParam(
    StyleType.borderColor,
    'color-line',
    CssColor.WstSystemLine1Color,
  ),
  tableSessionDividerOpacity: colorParamWithOpacity(
    StyleType.color,
    'color-line',
    0.3,
    CssColor.WstSystemLine1Color,
  ),
  generalWidgetBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-fill-background-primary',
    CssColor.WstPrimaryBackgroundColor,
  ),
  generalWidgetCornerRadius: numberParam(StyleType.borderRadius, 0),
  tableDayDividerWidth: numberParam(StyleType.width, 1),
  tableSessionDividerWidth: numberParam(StyleType.width, 1),
  headerTitleFont: fontParam(
    'Page-title',
    { size: 24 },
    CssFont.WstHeading2Font,
  ),
  headerTitleColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstHeading2Color,
  ),
  datePickerDatesFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  datePickerDatesColor: colorParam(
    StyleType.color,
    'color-text-primary',
    CssColor.WstParagraph2Color,
  ),
  selectionAndHighlightColor: colorParam(
    StyleType.backgroundColor,
    'color-text-primary',
    CssColor.WstPrimaryBackgroundColor,
  ),
  datePickerDisabledColor: colorParamWithOpacity(
    StyleType.color,
    'color-disabled',
    1,
    CssColor.WstSystemDisabledColor,
  ),
  datePickerSelectedColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-fill-background-primary',
    100,
    CssColor.WstPrimaryBackgroundColor,
  ),
  filtersFont: fontParam('Body-M', { size: 14 }, CssFont.WstParagraph2Font),
  filtersColor: colorParam(
    StyleType.color,
    'color-text-secondary',
    CssColor.WstParagraph2Color,
  ),
  headerTitleVisibility: booleanParam(true),
  headerServiceFilterVisibility: booleanParam(true),
  headerStaffFilterVisibility: booleanParam(true),
  headerLocationFilterVisibility: booleanParam(true),
  headerTimezoneSelectionVisibility: booleanParam(false),
  tableDayDividerVisibility: booleanParam(true),
  tableSessionDividerVisibility: booleanParam(true),
  sessionServiceImageVisibility: booleanParam(true),
  sessionDurationVisibility: booleanParam(true),
  // Design ticks this (Figma 4235-116621) but it has always shipped off.
  sessionStaffImageVisibility: coursesBooleanParam(false, true),
  sessionReadMoreLinkVisibility: booleanParam(false),
  sessionLocationVisibility: booleanParam(false),
  sessionStaffMemberVisibility: booleanParam(true),
  sessionPriceVisibility: coursesBooleanParam(false, true),
  headerServiceTypeFilterVisibility: booleanParam(true),
  headerCourseStatusFilterVisibility: booleanParam(true),
  sessionCourseBadgeVisibility: booleanParam(true),
  // Ships hidden on a fresh drop, like the location (Figma 4246-124335).
  sessionCourseStatusVisibility: booleanParam(false),
  sessionCourseDateRangeVisibility: booleanParam(true),
  sessionPriceBeforeDiscountVisibility: booleanParam(false),
  sessionDiscountNameVisibility: booleanParam(true),
  sessionNumberOfSpotsLeftVisibility: booleanParam(true),
  canBookFromDailyAgenda: booleanParam(true),
  dayLayoutPaddingBetweenHoursAndItemsOverlay: numberParam(StyleType.gap, 16),
  itemsLayoutItemHeightOverlay: numberParam(StyleType.height, 190),
  itemsLayoutSidePaddingOverlay: numberParam(StyleType.padding, 32),
  itemsLayoutTopAndBottomPadding: numberParam(StyleType.padding, 32),
  itemsLayoutSpaceBetweenItems: numberParam(StyleType.padding, 16),
};

const getItemsLayoutAlignment = (tpaData: any, layout?: LayoutTypes) => {
  const sessionItemsAlignment =
    layout === LayoutTypes.OVERLAY
      ? tpaData.sessionItemsAlignmentOverlay
      : tpaData.sessionItemsAlignment;

  switch (sessionItemsAlignment) {
    case AlignmentOptions.TOP:
      return 'flex-start';
    case AlignmentOptions.CENTER:
      return 'center';
    case AlignmentOptions.BOTTOM:
      return 'flex-end';
    default:
      return layout === LayoutTypes.OVERLAY ? 'flex-end' : 'flex-start';
  }
};

const getDisplayStyle = (value: boolean) => {
  return value ? DisplayStyles.UNSET : DisplayStyles.NONE;
};

const getDisplayStyleFromAny = (...values: boolean[]) => {
  return values.some(Boolean) ? DisplayStyles.UNSET : DisplayStyles.NONE;
};

export const customCssVars: CustomCssVarsFn<ICustomStyleParamsInput> = ({
  styleParams,
  tpaData,
}): ICustomStylesParam => {
  return {
    agendaAlignedItem: getItemsLayoutAlignment(tpaData),
    agendaAlignItemsOverlay: getItemsLayoutAlignment(
      tpaData,
      LayoutTypes.OVERLAY,
    ),
    displayHeaderTitle: getDisplayStyle(
      styleParams.booleans.headerTitleVisibility,
    ),
    displayHeaderServiceFilter: getDisplayStyle(
      styleParams.booleans.headerServiceFilterVisibility,
    ),
    displayHeaderStaffFilter: getDisplayStyle(
      styleParams.booleans.headerStaffFilterVisibility,
    ),
    displayHeaderLocationFilter: getDisplayStyle(
      styleParams.booleans.headerLocationFilterVisibility,
    ),
    displayHeaderTimezoneSelection: getDisplayStyle(
      styleParams.booleans.headerTimezoneSelectionVisibility,
    ),
    displaySessionServiceImage: getDisplayStyle(
      styleParams.booleans.sessionServiceImageVisibility,
    ),
    displaySessionDuration: getDisplayStyle(
      styleParams.booleans.sessionDurationVisibility,
    ),
    displaySessionStaffImage: getDisplayStyle(
      styleParams.booleans.sessionStaffImageVisibility,
    ),
    displaySessionReadMoreLink: getDisplayStyle(
      styleParams.booleans.sessionReadMoreLinkVisibility,
    ),
    displaySessionLocation: getDisplayStyle(
      styleParams.booleans.sessionLocationVisibility,
    ),
    displaySessionStaffMember: getDisplayStyle(
      styleParams.booleans.sessionStaffMemberVisibility,
    ),
    displaySessionStaffAndLocationWrapper: getDisplayStyleFromAny(
      styleParams.booleans.sessionStaffMemberVisibility,
      styleParams.booleans.sessionLocationVisibility,
    ),
    displaySessionPrice: getDisplayStyle(
      styleParams.booleans.sessionPriceVisibility,
    ),
    displayHeaderServiceTypeFilter: getDisplayStyle(
      styleParams.booleans.headerServiceTypeFilterVisibility,
    ),
    displayHeaderCourseStatusFilter: getDisplayStyle(
      styleParams.booleans.headerCourseStatusFilterVisibility,
    ),
    displaySessionCourseBadge: getDisplayStyle(
      styleParams.booleans.sessionCourseBadgeVisibility,
    ),
    displaySessionCourseStatus: getDisplayStyle(
      styleParams.booleans.sessionCourseStatusVisibility,
    ),
    displaySessionCourseDateRange: getDisplayStyle(
      styleParams.booleans.sessionCourseDateRangeVisibility,
    ),
    displaySessionPriceBeforeDiscount: getDisplayStyle(
      styleParams.booleans.sessionPriceBeforeDiscountVisibility,
    ),
    displaySessionDiscountName: getDisplayStyle(
      styleParams.booleans.sessionDiscountNameVisibility,
    ),
    displaySessionNumberOfSpotsLeft: getDisplayStyle(
      styleParams.booleans.sessionNumberOfSpotsLeftVisibility,
    ),
    displayTableDayDivider: getDisplayStyle(
      styleParams.booleans.tableDayDividerVisibility,
    ),
    displayTableSessionDivider: getDisplayStyle(
      styleParams.booleans.tableSessionDividerVisibility,
    ),
    displayBookFromDailyAgenda: getDisplayStyle(
      styleParams.booleans.canBookFromDailyAgenda,
    ),
    spaceBetweenSessions: styleParams.booleans.tableSessionDividerVisibility
      ? 0
      : styleParams.numbers.itemsLayoutSpaceBetweenItems,
  };
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);
